<template>
  <div class="whitelist-page">
    <div class="page-header">
      <h1>{{ $t('whitelist.title') }}</h1>
    </div>
    <div class="page-content">
      <!-- My Additions Section -->
      <div class="whitelist-section my-additions-section">
        <h2 @click="toggleAdditions" class="additions-header">
          {{ $t('whitelist.myAdditions') }}
          <span class="additions-count" v-if="myAdditions.length > 0">({{ myAdditions.length }})</span>
          <i class="expand_icon" :class="{ expanded: showAdditions }">▼</i>
        </h2>
        <div v-show="showAdditions" class="additions-list">
          <div v-if="myAdditions.length === 0" class="no-additions">
            <p>{{ $t('whitelist.noAdditions') }}</p>
          </div>
          <div v-else class="additions-items">
            <div 
              v-for="(item, index) in sortedAdditions" 
              :key="index" 
              class="addition-item"
            >
              <div class="addition-type">
                <span class="type-badge" :class="'type-' + item.type">
                  {{ getTypeLabel(item.type) }}
                </span>
              </div>
              <div class="addition-value">{{ item.value }}</div>
              <div class="addition-date">{{ formatDateString(item.date) }}</div>
            </div>
          </div>
        </div>
      </div>
      <!-- MD5 Whitelist Query -->
      <div class="whitelist-section">
        <h2>{{ $t('whitelist.md5Title') }}</h2>
        <div class="query-form">
          <div class="input-group">
            <input 
              v-model="md5Query" 
              :placeholder="$t('whitelist.md5Placeholder')"
              class="query-input"
              maxlength="32"
            />
          </div>
          <div class="button-group">
            <button 
              @click="queryMD5" 
              :disabled="!md5Query || md5Loading"
              class="query-button"
            >
              {{ md5Loading ? $t('whitelist.querying') : $t('whitelist.query') }}
            </button>
            <button 
              @click="addMD5" 
              :disabled="!md5Query || md5Adding"
              class="add-button"
            >
              {{ md5Adding ? $t('whitelist.adding') : $t('whitelist.add') }}
            </button>
          </div>
          <div v-if="md5Result" class="result-container">
            <div v-if="md5Result.exists" class="result-success">
              <p><strong>{{ $t('whitelist.status') }}:</strong> {{ md5Result.data.scan }}</p>
              <p v-if="md5Result.data.last_update">
                <strong>{{ $t('whitelist.lastUpdate') }}:</strong> {{ formatDate(md5Result.data.last_update) }}
              </p>
              <p v-if="md5Result.data.score !== undefined">
                <strong>{{ $t('whitelist.score') }}:</strong> {{ md5Result.data.score }}
              </p>
            </div>
            <div v-else class="result-not-found">
              <p>{{ md5Result.message }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- AppID Whitelist Query -->
      <div class="whitelist-section">
        <h2>{{ $t('whitelist.appidTitle') }}</h2>
        <div class="query-form">
          <div class="input-group">
            <input 
              v-model="appidQuery" 
              :placeholder="$t('whitelist.appidPlaceholder')"
              class="query-input"
            />
          </div>
          <div class="button-group">
            <button 
              @click="queryAppID" 
              :disabled="!appidQuery || appidLoading"
              class="query-button"
            >
              {{ appidLoading ? $t('whitelist.querying') : $t('whitelist.query') }}
            </button>
            <button 
              @click="addAppID" 
              :disabled="!appidQuery || appidAdding"
              class="add-button"
            >
              {{ appidAdding ? $t('whitelist.adding') : $t('whitelist.add') }}
            </button>
          </div>
          <div v-if="appidResult" class="result-container">
            <div v-if="appidResult.exists" class="result-success">
              <p><strong>{{ $t('whitelist.appid') }}:</strong> {{ appidResult.data.appid }}</p>
              <p v-if="appidResult.data.date">
                <strong>{{ $t('whitelist.updateDate') }}:</strong> {{ appidResult.data.date }}
              </p>
            </div>
            <div v-else class="result-not-found">
              <p>{{ appidResult.message }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Certificate SHA1 Whitelist Query -->
      <div class="whitelist-section">
        <h2>{{ $t('whitelist.certTitle') }}</h2>
        <div class="query-form">
          <div class="input-group">
            <input 
              v-model="certQuery" 
              :placeholder="$t('whitelist.certPlaceholder')"
              class="query-input"
              maxlength="40"
            />
          </div>
          <div class="button-group">
            <button 
              @click="queryCert" 
              :disabled="!certQuery || certLoading"
              class="query-button"
            >
              {{ certLoading ? $t('whitelist.querying') : $t('whitelist.query') }}
            </button>
            <button 
              @click="addCert" 
              :disabled="!certQuery || certAdding"
              class="add-button"
            >
              {{ certAdding ? $t('whitelist.adding') : $t('whitelist.add') }}
            </button>
          </div>
          <div v-if="certResult" class="result-container">
            <div v-if="certResult.exists" class="result-success">
              <p><strong>{{ $t('whitelist.certSha1') }}:</strong> {{ certResult.data.cert_sha1 }}</p>
              <p v-if="certResult.data.date">
                <strong>{{ $t('whitelist.updateDate') }}:</strong> {{ certResult.data.date }}
              </p>
            </div>
            <div v-else class="result-not-found">
              <p>{{ certResult.message }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios'
  import { Message } from 'element-ui'
  
  export default {
    name: 'Whitelist',
    data() {
      return {
        md5Query: '',
        appidQuery: '',
        certQuery: '',
        md5Result: null,
        appidResult: null,
        certResult: null,
        md5Loading: false,
        appidLoading: false,
        certLoading: false,
        md5Adding: false,
        appidAdding: false,
        certAdding: false,
        myAdditions: [],
        showAdditions: false,
        loadingAdditions: false
      }
    },
    mounted() {
      // Check if user has whitelist permission
      this.checkWhitelistPermission()
      // Load user's additions
      this.loadMyAdditions()
    },
    computed: {
      sortedAdditions() {
        // Sort by date, newest first
        return [...this.myAdditions].sort((a, b) => {
          return new Date(b.date) - new Date(a.date)
        })
      }
    },
    methods: {
      checkWhitelistPermission: async function() {
        try {
          const response = await axios.get('/api/user')
          if (!response.data.whitelist) {
            // User doesn't have whitelist permission, redirect to dashboard
            this.$router.push('/dashboard').catch(err => {
              if (err.name !== 'NavigationDuplicated') {
                console.error(err)
              }
            })
          }
        } catch (err) {
          console.error('Error checking whitelist permission:', err)
          this.$router.push('/dashboard').catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error(err)
            }
          })
        }
      },
      queryMD5: async function() {
        if (!this.md5Query.trim()) {
          return
        }
        this.md5Loading = true
        this.md5Result = null
        try {
          const response = await axios.get(`/api/whitelist/md5/${this.md5Query.trim()}`)
          this.md5Result = response.data
        } catch (err) {
          Message({
            type: 'error',
            message: err.response && err.response.data && err.response.data.message 
              ? err.response.data.message 
              : (this.$store.state.lang === 'en' ? 'Failed to query MD5 whitelist' : '查询MD5白名单失败'),
            showClose: true
          })
          console.error('Error querying MD5 whitelist:', err)
        } finally {
          this.md5Loading = false
        }
      },
      queryAppID: async function() {
        if (!this.appidQuery.trim()) {
          return
        }
        this.appidLoading = true
        this.appidResult = null
        try {
          const response = await axios.get(`/api/whitelist/appid/${encodeURIComponent(this.appidQuery.trim())}`)
          this.appidResult = response.data
        } catch (err) {
          Message({
            type: 'error',
            message: err.response && err.response.data && err.response.data.message 
              ? err.response.data.message 
              : (this.$store.state.lang === 'en' ? 'Failed to query AppID whitelist' : '查询AppID白名单失败'),
            showClose: true
          })
          console.error('Error querying AppID whitelist:', err)
        } finally {
          this.appidLoading = false
        }
      },
      queryCert: async function() {
        if (!this.certQuery.trim()) {
          return
        }
        this.certLoading = true
        this.certResult = null
        try {
          const response = await axios.get(`/api/whitelist/cert/${this.certQuery.trim()}`)
          this.certResult = response.data
        } catch (err) {
          Message({
            type: 'error',
            message: err.response && err.response.data && err.response.data.message 
              ? err.response.data.message 
              : (this.$store.state.lang === 'en' ? 'Failed to query Certificate whitelist' : '查询证书白名单失败'),
            showClose: true
          })
          console.error('Error querying Cert whitelist:', err)
        } finally {
          this.certLoading = false
        }
      },
      formatDate: function(timestamp) {
        if (!timestamp) return ''
        const date = new Date(parseInt(timestamp))
        return date.toLocaleString()
      },
      formatDateString: function(dateString) {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleString()
      },
      toggleAdditions: function() {
        this.showAdditions = !this.showAdditions
        if (this.showAdditions && this.myAdditions.length === 0) {
          this.loadMyAdditions()
        }
      },
      loadMyAdditions: async function() {
        this.loadingAdditions = true
        try {
          const response = await axios.get('/api/whitelist/my-additions')
          this.myAdditions = response.data.data || []
        } catch (err) {
          console.error('Error loading whitelist additions:', err)
        } finally {
          this.loadingAdditions = false
        }
      },
      getTypeLabel: function(type) {
        const labels = {
          md5: this.$store.state.lang === 'en' ? 'MD5' : 'MD5',
          appid: this.$store.state.lang === 'en' ? 'AppID' : 'AppID',
          cert: this.$store.state.lang === 'en' ? 'Certificate' : '证书'
        }
        return labels[type] || type
      },
      addMD5: async function() {
        if (!this.md5Query.trim()) {
          return
        }
        
        // Validate MD5 length
        if (this.md5Query.trim().length !== 32) {
          Message({
            type: 'error',
            message: this.$store.state.lang === 'en' 
              ? 'MD5 must be exactly 32 characters long' 
              : 'MD5必须恰好为32个字符',
            showClose: true
          })
          return
        }
        
        // Validate MD5 format (hexadecimal)
        if (!/^[0-9A-Fa-f]{32}$/.test(this.md5Query.trim())) {
          Message({
            type: 'error',
            message: this.$store.state.lang === 'en' 
              ? 'MD5 must contain only hexadecimal characters (0-9, A-F)' 
              : 'MD5只能包含十六进制字符 (0-9, A-F)',
            showClose: true
          })
          return
        }
        
        this.md5Adding = true
        try {
          const response = await axios.post('/api/whitelist/md5', {
            md5: this.md5Query.trim()
          })
          Message({
            type: 'success',
            message: response.data.message,
            showClose: true
          })
          const addedMD5 = this.md5Query.trim()
          // Refresh additions list
          await this.loadMyAdditions()
          // Refresh query if the added MD5 matches the query
          if (this.md5Query.trim().toUpperCase() === addedMD5.toUpperCase()) {
            await this.queryMD5()
          }
        } catch (err) {
          const errorMessage = err.response && err.response.data && err.response.data.message 
            ? err.response.data.message 
            : (this.$store.state.lang === 'en' ? 'Failed to add MD5 to whitelist' : '添加MD5到白名单失败')
          Message({
            type: 'error',
            message: errorMessage,
            showClose: true
          })
          console.error('Error adding MD5 to whitelist:', err)
        } finally {
          this.md5Adding = false
        }
      },
      addAppID: async function() {
        if (!this.appidQuery.trim()) {
          return
        }
        
        this.appidAdding = true
        try {
          const response = await axios.post('/api/whitelist/appid', {
            appid: this.appidQuery.trim()
          })
          Message({
            type: 'success',
            message: response.data.message,
            showClose: true
          })
          const addedAppID = this.appidQuery.trim()
          // Refresh additions list
          await this.loadMyAdditions()
          // Refresh query if the added AppID matches the query
          if (this.appidQuery.trim() === addedAppID) {
            await this.queryAppID()
          }
        } catch (err) {
          const errorMessage = err.response && err.response.data && err.response.data.message 
            ? err.response.data.message 
            : (this.$store.state.lang === 'en' ? 'Failed to add AppID to whitelist' : '添加AppID到白名单失败')
          Message({
            type: 'error',
            message: errorMessage,
            showClose: true
          })
          console.error('Error adding AppID to whitelist:', err)
        } finally {
          this.appidAdding = false
        }
      },
      addCert: async function() {
        if (!this.certQuery.trim()) {
          return
        }
        
        // Validate SHA1 length
        if (this.certQuery.trim().length !== 40) {
          Message({
            type: 'error',
            message: this.$store.state.lang === 'en' 
              ? 'Certificate SHA1 must be exactly 40 characters long' 
              : '证书SHA1必须恰好为40个字符',
            showClose: true
          })
          return
        }
        
        // Validate SHA1 format (hexadecimal)
        if (!/^[0-9A-Fa-f]{40}$/.test(this.certQuery.trim())) {
          Message({
            type: 'error',
            message: this.$store.state.lang === 'en' 
              ? 'Certificate SHA1 must contain only hexadecimal characters (0-9, A-F)' 
              : '证书SHA1只能包含十六进制字符 (0-9, A-F)',
            showClose: true
          })
          return
        }
        
        this.certAdding = true
        try {
          const response = await axios.post('/api/whitelist/cert', {
            certSha1: this.certQuery.trim()
          })
          Message({
            type: 'success',
            message: response.data.message,
            showClose: true
          })
          const addedCert = this.certQuery.trim()
          // Refresh additions list
          await this.loadMyAdditions()
          // Refresh query if the added Cert matches the query
          if (this.certQuery.trim().toUpperCase() === addedCert.toUpperCase()) {
            await this.queryCert()
          }
        } catch (err) {
          const errorMessage = err.response && err.response.data && err.response.data.message 
            ? err.response.data.message 
            : (this.$store.state.lang === 'en' ? 'Failed to add Certificate to whitelist' : '添加证书到白名单失败')
          Message({
            type: 'error',
            message: errorMessage,
            showClose: true
          })
          console.error('Error adding Cert to whitelist:', err)
        } finally {
          this.certAdding = false
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .whitelist-page {
    padding: 20px;
    min-height: calc(100vh - 70px);
    background: #f3f6fe;
    
    .page-header {
      margin-bottom: 24px;
      
      h1 {
        font-size: 24px;
        font-weight: bold;
        color: #2c3e50;
        margin: 0;
      }
    }
    
    .page-content {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }
    
    .whitelist-section {
      background: white;
      border-radius: 6px;
      padding: 24px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      
      h2 {
        font-size: 18px;
        font-weight: bold;
        color: #2c3e50;
        margin: 0 0 20px 0;
        padding-bottom: 12px;
        border-bottom: 2px solid #f0f0f0;
      }
      
      &.my-additions-section {
        h2.additions-header {
          cursor: pointer;
          user-select: none;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: opacity 0.2s;
          
          &:hover {
            opacity: 0.8;
          }
          
          .additions-count {
            font-size: 14px;
            font-weight: normal;
            color: #999;
          }
          
          .expand_icon {
            margin-left: auto;
            font-size: 12px;
            color: #666;
            transition: transform 0.3s ease;
            
            &.expanded {
              transform: rotate(180deg);
            }
          }
        }
        
        .additions-list {
          animation: slideDown 0.3s ease;
          
          .no-additions {
            text-align: center;
            padding: 40px 20px;
            color: #999;
          }
          
          .additions-items {
            display: flex;
            flex-direction: column;
            gap: 12px;
            
            .addition-item {
              display: grid;
              grid-template-columns: 100px 1fr auto;
              gap: 16px;
              padding: 12px;
              border: 1px solid #e0e0e0;
              border-radius: 4px;
              align-items: center;
              
              .addition-type {
                .type-badge {
                  display: inline-block;
                  padding: 4px 12px;
                  border-radius: 12px;
                  font-size: 12px;
                  font-weight: bold;
                  
                  &.type-md5 {
                    background-color: #e3f2fd;
                    color: #1976d2;
                  }
                  
                  &.type-appid {
                    background-color: #f3e5f5;
                    color: #7b1fa2;
                  }
                  
                  &.type-cert {
                    background-color: #e8f5e9;
                    color: #388e3c;
                  }
                }
              }
              
              .addition-value {
                font-family: monospace;
                font-size: 13px;
                color: #424242;
                word-break: break-all;
              }
              
              .addition-date {
                font-size: 12px;
                color: #999;
                white-space: nowrap;
              }
            }
          }
        }
      }
      
      .query-form {
        .input-group {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
            
            .query-input {
              flex: 1;
            }
        }
        
          .button-group {
            display: flex;
            gap: 12px;
            margin-bottom: 16px;
          }
          
          .query-input {
          padding: 10px 12px;
          border: 1px solid #d1d4d9;
          border-radius: 4px;
          font-size: 14px;
          
          &:focus {
            outline: none;
            border-color: #409eff;
          }
        }
        
          .query-button {
          padding: 10px 24px;
          border: none;
          border-radius: 4px;
          background-color: #409eff;
          color: white;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
          
          &:hover:not(:disabled) {
            background-color: #66b1ff;
          }
          
          &:disabled {
            background-color: #c0c4cc;
            cursor: not-allowed;
          }
        }
        
        .add-button {
          padding: 10px 24px;
          border: none;
          border-radius: 4px;
          background-color: #67c23a;
          color: white;
          font-size: 14px;
          cursor: pointer;
          transition: background-color 0.2s;
          
          &:hover:not(:disabled) {
            background-color: #85ce61;
          }
          
          &:disabled {
            background-color: #c0c4cc;
            cursor: not-allowed;
          }
        }
        
        .result-container {
          margin-top: 16px;
          padding: 16px;
          border-radius: 4px;
          
          .result-success {
            background-color: #f0f9ff;
            border: 1px solid #b3d8ff;
            color: #2c3e50;
            
            p {
              margin: 8px 0;
              font-size: 14px;
              
              strong {
                color: #409eff;
                margin-right: 8px;
              }
            }
          }
          
          .result-not-found {
            background-color: #fef0f0;
            border: 1px solid #fbc4c4;
            color: #f56c6c;
            
            p {
              margin: 0;
              font-size: 14px;
            }
          }
        }
      }
    }
  }

  
  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 1000px;
    }
  }
  
  @media screen and (max-width: 760px) {
    .whitelist-page {
      padding: 12px;
      
      .whitelist-section {
        padding: 16px;
        
        .query-form {
          .input-group {
            flex-direction: column;
            
            .query-button {
              width: 100%;
            }
          }
        }
        
        &.my-additions-section {
          .additions-list {
            .additions-items {
              .addition-item {
                grid-template-columns: 1fr;
                gap: 8px;
                
                .addition-type {
                  grid-row: 1;
                }
                
                .addition-value {
                  grid-row: 2;
                }
                
                .addition-date {
                  grid-row: 3;
                }
              }
            }
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/views/service/whitelist.vue